import { formatMoney } from "@bw/shared/utils";
import { ApiError } from "@bw/shared/api";
import { Alert, Button, CircularProgress, Chip, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { isTransactionId, useTransactionDetail } from "../hooks/useTransactionDetail";
import { transactionLabel } from "../transactionPresentation";

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "UTC",
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** The payer's exact, server-scoped ledger confirmation—not a tax invoice. */
export function TransactionDetailPage() {
  const { transactionId } = useParams<{ transactionId: string }>();
  const detail = useTransactionDetail(transactionId);
  const transaction = detail.data;
  const title = transaction?.type === "refund" || transaction?.type === "partial_refund"
    ? "Refund confirmation"
    : "Payment confirmation";
  const notFound =
    !isTransactionId(transactionId) ||
    (detail.error instanceof ApiError && detail.error.status === 404);

  return (
    <Page title={title}>
      <Stack spacing={2.5} sx={{ maxWidth: 640, alignItems: "flex-start" }}>
        {detail.isLoading ? (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }} role="status">
            <CircularProgress size={20} />
            <Typography color="text.secondary">Loading confirmation…</Typography>
          </Stack>
        ) : notFound ? (
          <Typography>Payment confirmation not found.</Typography>
        ) : detail.isError ? (
          <Alert severity="error">
            We couldn&apos;t load this confirmation. Please try again.
          </Alert>
        ) : transaction ? (
          <>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {formatMoney(transaction.amount, transaction.currency)}
            </Typography>
            <Typography>{transactionLabel(transaction)}</Typography>
            <Chip label={transaction.status} size="small" variant="outlined" />
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">Facility</Typography>
              <Typography>{transaction.merchant_name || "Facility name unavailable"}</Typography>
            </Stack>
            <Typography
              component="time"
              dateTime={transaction.created_at}
              color="text.secondary"
            >
              {formatDate(transaction.created_at)}
            </Typography>
            {transaction.confirmation_number ? (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Confirmation number
                </Typography>
                <Typography sx={{ overflowWrap: "anywhere" }}>
                  {transaction.confirmation_number}
                </Typography>
              </Stack>
            ) : null}
          </>
        ) : null}
        <Button component={RouterLink} to="/me/transactions" variant="text" size="small">
          All transactions
        </Button>
      </Stack>
    </Page>
  );
}
